import { useContext, useEffect, useRef, useState } from "react";
import { Users, Calendar, PhoneCall } from "lucide-react";
import { useApiCall } from "../../../component/custom-hooks/api-call";
import Skeleton from "@mui/material/Skeleton";
import {
  Table,
  TableBody,
  TableCard,
  TableCell,
  TableHead,
  TableHeaderWrap,
  TableRow,
} from "../../../component/table/table-layout";
import { CustomDropdown } from "../../../component/ui/dropdown/CustomDropdown";
import { CustomDropdownButton } from "../../../component/ui/dropdown/CustomDropdownButton";
import { CustomDropdownMenu } from "../../../component/ui/dropdown/CustomDropdownMenu";
import { CustomDropdownMenuList } from "../../../component/ui/dropdown/CustomDropdownMenuList";
import { TitleHeading } from "../../../component/ui/layout";
import moment from "moment";
import { GlobalContext } from "../../../context/Context";
import { FeildWraper } from "../../../component/form/form-layout";
import InnerLoader from "../../../component/ui/loader/inner-loader";
import DataNotFound from "../../../component/ui/loader/data-not-found";
import {
  StatCard,
  StatsCardSkeleton,
} from "../../../component/LeadsDashboardComponents/StatCard";
import {
  Clander,
  Clock,
  RoundArrowCircle,
  TeamAvailability,
} from "../../../component/LeadsDashboardComponents/StatCardIcons";
import Tooltip from "../../../component/ui/tooltip";
import { ChartMainCard } from "../../../component/LeadsDashboardComponents/ChartsHeader";

export default function AgentMainDashboard() {
  let { state } = useContext(GlobalContext);
  const apiCall = useApiCall();

  const [dashboardData, setDashboardData] = useState({});
  const [isDashboardApiLoaded, setIsDashboardApiLoaded] = useState(false);

  const [selectedMonth, setSelectedMonth] = useState(
    moment(new Date()).format("YYYY-MM"),
  );
  const [defaultMonthOption, setDefaultMonthOption] = useState("");

  const getDashboardData = async () => {
    setIsDashboardApiLoaded(false);
    try {
      const resp = await apiCall({
        method: "get",
        url: `get-agent-overview?date_range=${defaultMonthOption || selectedMonth}`,
      });
      console.log("Agents OVerivw", resp);

      setDashboardData(resp);
      setIsDashboardApiLoaded(true);
    } catch (error) {
      console.warn("Error : ", error);
    }
  };

  useEffect(() => {
    getDashboardData();
  }, [selectedMonth, defaultMonthOption]);

  const inputRef = useRef();

  const openMonthPicker = () => {
    if (inputRef.current?.showPicker) {
      inputRef.current.showPicker();
    } else {
      inputRef.current?.focus();
      inputRef.current?.click();
    }
  };

  return (
    <>
      <div className="pt-[20px] h-full overflow-hidden">
        <div className="w-full h-full overflow-hidden flex flex-col gap-[20px]">
          {/* Header */}
          <div className="flex items-center justify-between">
            {/* <TitleHeading>My Lead Overview</TitleHeading> */}
            <div>
              <h1 className="text-2xl font-bold">My Lead Overview</h1>
            </div>
            <div className="flex gap-3 flex-shrink-0 w-fit items-center justify-end">
              <FeildWraper className={"!w-fit !flex-[0_0_auto] !pl-0 !py-0"}>
                <div className="relative">
                  <input
                    type="month"
                    ref={inputRef}
                    className="absolute opacity-0 pointer-events-none"
                    max={moment(new Date()).format("YYYY-MM")}
                    value={selectedMonth}
                    onChange={(e) => {
                      setDefaultMonthOption("");
                      setSelectedMonth(e.target.value);
                    }}
                  />

                  <CustomDropdown
                    position="bottom-right"
                    className="myDropdown"
                  >
                    <CustomDropdownButton arrow={false} className="btnClass">
                      <h3 className="!text-[16px] px-5 py-1 capitalize flex items-center gap-1">
                        <Calendar className="!w-4 !h-4" />
                        {defaultMonthOption
                          ? defaultMonthOption?.replaceAll("_", " ")
                          : moment(selectedMonth, "YYYY-MM").format(
                              "MMMM YYYY",
                            )}
                      </h3>
                    </CustomDropdownButton>

                    <CustomDropdownMenu className="w-full">
                      <CustomDropdownMenuList
                        onClick={() => {
                          setDefaultMonthOption("this_month");
                          setSelectedMonth("");
                        }}
                      >
                        <div
                          className={
                            "d-flex whitespace-normal align-items-center gap-1"
                          }
                        >
                          This Month
                        </div>
                      </CustomDropdownMenuList>

                      <CustomDropdownMenuList
                        onClick={() => {
                          setDefaultMonthOption("last_month");
                          setSelectedMonth("");
                        }}
                      >
                        <div
                          className={
                            "d-flex whitespace-normal align-items-center gap-1"
                          }
                        >
                          Last Month
                        </div>
                      </CustomDropdownMenuList>

                      <CustomDropdownMenuList
                        onClick={() => {
                          setDefaultMonthOption("this_year");
                          setSelectedMonth("");
                        }}
                      >
                        <div
                          className={
                            "d-flex whitespace-normal align-items-center gap-1"
                          }
                        >
                          This Year
                        </div>
                      </CustomDropdownMenuList>

                      <CustomDropdownMenuList
                        onClick={() => {
                          setDefaultMonthOption("last_year");
                          setSelectedMonth("");
                        }}
                      >
                        <div
                          className={
                            "d-flex whitespace-normal align-items-center gap-1"
                          }
                        >
                          Last Year
                        </div>
                      </CustomDropdownMenuList>

                      <CustomDropdownMenuList onClick={openMonthPicker}>
                        <div
                          className={
                            "d-flex whitespace-normal align-items-center gap-1"
                          }
                        >
                          Custom Month
                        </div>
                      </CustomDropdownMenuList>
                    </CustomDropdownMenu>
                  </CustomDropdown>
                </div>
              </FeildWraper>
            </div>
          </div>

          {/* KPI Row */}
          <TableCard className={"h-full flex"}>
            <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-5 gap-4">
              {isDashboardApiLoaded ? (
                <>
                  <StatCard
                    icon={Clock}
                    iconBgColor="#BBF7D0"
                    iconColor={"#16A34A"}
                    title="Total"
                    value={dashboardData?.metrics?.total_pipeline}
                  />
                  <StatCard
                    icon={Users}
                    iconColor={"#FF7F50"}
                    iconBgColor="#FFEBE0"
                    title="Leads Assigned"
                    value={dashboardData?.metrics?.lead_assigned}
                  />
                  <StatCard
                    icon={Clander}
                    iconColor={"#335CFF"}
                    iconBgColor="#DBEAFE"
                    title="Conversions"
                    value={dashboardData?.metrics?.conversions}
                  />
                  <StatCard
                    icon={PhoneCall}
                    iconBgColor="#EDE9FE"
                    iconColor={"#6D28D9"}
                    title="Calls Made"
                    value={dashboardData?.metrics?.call_made}
                  />

                  <StatCard
                    icon={RoundArrowCircle}
                    iconColor={"#FF4040"}
                    iconBgColor="#FEE2E2"
                    title="My Conversions"
                    value={`${dashboardData?.metrics?.your_conversion_rate}%`}
                  />
                </>
              ) : (
                Array.from({ length: 5 }).map((_, index) => (
                  // <div
                  //   key={index}
                  //   className="bg-white shadow rounded-xl p-4 animate-pulse"
                  // >
                  //   {/* Top row: label + icon */}
                  //   <div className="flex items-center justify-between mb-2">
                  //     <Skeleton variant="text" width={80} height={16} />
                  //     <Skeleton variant="circular" width={20} height={20} />
                  //   </div>
                  //   {/* Value row */}
                  //   <div className="flex items-center justify-between gap-2 mb-1">
                  //     <Skeleton variant="text" width={60} height={32} />
                  //     <Skeleton variant="rounded" width={40} height={20} />
                  //   </div>
                  //   {/* Sublabel */}
                  //   <Skeleton variant="text" width={80} height={12} />
                  // </div>
                  <StatsCardSkeleton />
                ))
              )}
            </div>

            <div className="flex flex-col gap-6 mt-5">
              {/* className="grid grid-cols-1 md:grid-cols-6 gap-6 mt-5" */}
              {/* <div className=" ">
                <div className="pb-4">
                  <TitleHeading>Active Leads</TitleHeading>
                </div>
                <div className="p-5 h-[400px]  bg-white border border-[#E1E4EA] shadow-card rounded-[10px] overflow-hidden">
                  <TableCard>
                    {isDashboardApiLoaded ? (
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell header>
                              <TableHeaderWrap>
                                <span>Name</span>
                              </TableHeaderWrap>
                            </TableCell>

                            <TableCell header>
                              <TableHeaderWrap>
                                <span>Product</span>
                              </TableHeaderWrap>
                            </TableCell>
                            <TableCell header>
                              <TableHeaderWrap>
                                <span>Last Contact</span>
                              </TableHeaderWrap>
                            </TableCell>

                            <TableCell header>
                              <TableHeaderWrap>
                                <span>Status</span>
                              </TableHeaderWrap>
                            </TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {dashboardData?.active_leads?.length ? (
                            dashboardData?.active_leads?.map((eachPlat, i) => (
                              <TableRow key={i}>
                                <TableCell>
                                  <TableHeaderWrap>
                                    <span>{eachPlat?.client_name || "-"}</span>
                                  </TableHeaderWrap>
                                </TableCell>

                                <TableCell>
                                  <TableHeaderWrap>
                                    <span>{eachPlat?.product || "-"}</span>
                                  </TableHeaderWrap>
                                </TableCell>

                                <TableCell>
                                  <TableHeaderWrap>
                                    <span>{eachPlat?.last_contact || "-"}</span>
                                  </TableHeaderWrap>
                                </TableCell>

                                <TableCell>
                                  <TableHeaderWrap>
                                    <span>
                                      {eachPlat?.status ? (
                                        <span
                                          className="inline-flex py-[4px] px-[12px] rounded-[6px] text-[10px] font-bold uppercase"
                                          style={
                                            eachPlat?.status?.toLowerCase() ==
                                            "completed"
                                              ? {
                                                  background: "#e8f5e9",
                                                  color: "#388e3c",
                                                }
                                              : eachPlat?.status?.toLowerCase() ==
                                                  "pending"
                                                ? {
                                                    background: "#dbe0f5",
                                                    color: "#335CFF",
                                                  }
                                                : eachPlat?.status?.toLowerCase() ==
                                                    "overdue"
                                                  ? {
                                                      background: "#fff3e0",
                                                      color: "#e65100",
                                                    }
                                                  : {
                                                      background: "#fff3e0",
                                                      color: "#e65100",
                                                    }
                                          }
                                        >
                                          {eachPlat.status}
                                        </span>
                                      ) : (
                                        "-"
                                      )}
                                    </span>
                                  </TableHeaderWrap>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={99}>
                                <DataNotFound
                                  title={"Data"}
                                  className={"text-center"}
                                />
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="flex justify-center items-center h-full">
                        <InnerLoader />
                      </div>
                    )}
                  </TableCard>
                </div>
              </div> */}
              <ChartMainCard
                Icon={<TeamAvailability />}
                iconColor={"bg-theme-green-light text-theme-green"}
                heading="Active Leads"
              >
                {isDashboardApiLoaded ? (
                  <div className="max-h-[430px] min-h-[430px] overflow-auto">
                    {dashboardData?.active_leads?.length > 0 ? (
                      <table className="min-w-full">
                        <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Product
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Last Contact
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Status
                            </th>
                          </tr>
                        </thead>

                        <tbody className="divide-y divide-gray-200">
                          {dashboardData?.active_leads?.map((eachPlat, i) => (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="px-6 py-4">
                                <h6 className="font-semibold text-sm">
                                  {eachPlat?.client_name?.trim()
                                    ? eachPlat.client_name
                                    : "-"}
                                </h6>
                              </td>
                              <td className="px-6 py-4">
                                <h6 className="font-medium text-sm">
                                  {eachPlat?.product ?? "-"}
                                </h6>
                              </td>
                              <td className="px-6 py-4">
                                {eachPlat?.last_contact ?? "-"}
                              </td>
                              <td className="px-6 py-4">
                                <span>
                                  {eachPlat?.status ? (
                                    <span
                                      className="inline-flex py-[4px] px-[12px] rounded-[6px] text-[10px] font-bold uppercase"
                                      style={
                                        eachPlat?.status?.toLowerCase() ==
                                        "completed"
                                          ? {
                                              background: "#e8f5e9",
                                              color: "#388e3c",
                                            }
                                          : eachPlat?.status?.toLowerCase() ==
                                              "pending"
                                            ? {
                                                background: "#dbe0f5",
                                                color: "#335CFF",
                                              }
                                            : eachPlat?.status?.toLowerCase() ==
                                                "overdue"
                                              ? {
                                                  background: "#fff3e0",
                                                  color: "#e65100",
                                                }
                                              : {
                                                  background: "#fff3e0",
                                                  color: "#e65100",
                                                }
                                      }
                                    >
                                      {eachPlat.status}
                                    </span>
                                  ) : (
                                    "-"
                                  )}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <DataNotFound title={"Leads"} />
                    )}
                  </div>
                ) : (
                  <InnerLoader />
                )}
              </ChartMainCard>

              {/* <div>
                <div className="pb-4">
                  <TitleHeading>Due Leads</TitleHeading>
                </div>
                <div className="p-5 h-[400px]  bg-white border border-[#E1E4EA] shadow-card rounded-[10px] overflow-hidden">
                  <TableCard>
                    {isDashboardApiLoaded ? (
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell header>
                              <TableHeaderWrap>
                                <span>Name</span>
                              </TableHeaderWrap>
                            </TableCell>

                            <TableCell header>
                              <TableHeaderWrap>
                                <span>Phone</span>
                              </TableHeaderWrap>
                            </TableCell>
                            <TableCell header>
                              <TableHeaderWrap>
                                <span>Due Date</span>
                              </TableHeaderWrap>
                            </TableCell>
                            <TableCell header>
                              <TableHeaderWrap>
                                <span>Notes</span>
                              </TableHeaderWrap>
                            </TableCell>

                            <TableCell header>
                              <TableHeaderWrap>
                                <span>Status</span>
                              </TableHeaderWrap>
                            </TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {dashboardData?.callbacks_due_leads?.length ? (
                            dashboardData?.callbacks_due_leads?.map(
                              (eachPlat, i) => (
                                <TableRow key={i}>
                                  <TableCell>
                                    <TableHeaderWrap>
                                      <span>
                                        {eachPlat?.client_name || "-"}
                                      </span>
                                    </TableHeaderWrap>
                                  </TableCell>

                                  <TableCell>
                                    <TableHeaderWrap>
                                      <span>{eachPlat?.phone || "-"}</span>
                                    </TableHeaderWrap>
                                  </TableCell>

                                  <TableCell>
                                    <TableHeaderWrap>
                                      <span>{eachPlat?.due_date || "-"}</span>
                                    </TableHeaderWrap>
                                  </TableCell>
                                  <TableCell>
                                    <TableHeaderWrap>
                                      {eachPlat?.notes?.length > 50 ? (
                                        <Tooltip
                                          key={`${i}-asideBottomItems`}
                                          content={eachPlat?.notes}
                                          position={"top center"}
                                        >
                                          {eachPlat?.notes?.slice(0, 50)}...
                                        </Tooltip>
                                      ) : (
                                        <span>{eachPlat?.notes || "-"}</span>
                                      )}
                                    </TableHeaderWrap>
                                  </TableCell>

                                  <TableCell>
                                    <TableHeaderWrap>
                                      <span>
                                        {eachPlat?.status ? (
                                          <span
                                            className="inline-flex py-[4px] px-[12px] rounded-[6px] text-[10px] font-bold uppercase"
                                            style={
                                              eachPlat?.status?.toLowerCase() ==
                                              "completed"
                                                ? {
                                                    background: "#e8f5e9",
                                                    color: "#388e3c",
                                                  }
                                                : eachPlat?.status?.toLowerCase() ==
                                                    "pending"
                                                  ? {
                                                      background: "#dbe0f5",
                                                      color: "#335CFF",
                                                    }
                                                  : eachPlat?.status?.toLowerCase() ==
                                                      "overdue"
                                                    ? {
                                                        background: "#fff3e0",
                                                        color: "#e65100",
                                                      }
                                                    : {
                                                        background: "#fff3e0",
                                                        color: "#e65100",
                                                      }
                                            }
                                          >
                                            {eachPlat.status}
                                          </span>
                                        ) : (
                                          ""
                                        )}
                                      </span>
                                    </TableHeaderWrap>
                                  </TableCell>
                                </TableRow>
                              ),
                            )
                          ) : (
                            <TableRow>
                              <TableCell colSpan={99}>
                                <DataNotFound
                                  title={"Data"}
                                  className={"text-center"}
                                />
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    ) : (
                      <InnerLoader />
                    )}
                  </TableCard>
                </div>
              </div> */}
              <ChartMainCard
                Icon={<TeamAvailability />}
                iconColor={"bg-[#FEE2E2] text-[#FF4040]"}
                heading="Due Leads"
              >
                {isDashboardApiLoaded ? (
                  <div className="max-h-[430px] min-h-[430px] overflow-auto">
                    {dashboardData?.callbacks_due_leads?.length > 0 ? (
                      <table className="min-w-full">
                        <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Phone
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Due Date
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Notes
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Status
                            </th>
                          </tr>
                        </thead>

                        <tbody className="divide-y divide-gray-200">
                          {dashboardData?.callbacks_due_leads?.map(
                            (eachPlat, i) => (
                              <tr key={i} className="hover:bg-gray-50">
                                <td className="px-6 py-4">
                                  <h6 className="font-semibold text-sm">
                                    {eachPlat?.client_name?.trim()
                                      ? eachPlat.client_name
                                      : "-"}
                                  </h6>
                                </td>
                                <td className="px-6 py-4">
                                  <h6 className="font-medium text-sm">
                                    {eachPlat?.phone ?? "-"}
                                  </h6>
                                </td>
                                <td className="px-6 py-4">
                                  {eachPlat?.due_date ?? "-"}
                                </td>
                                <td className="px-6 py-4">
                                  {eachPlat?.notes?.length > 50 ? (
                                    <Tooltip
                                      key={`${i}-asideBottomItems`}
                                      content={eachPlat?.notes}
                                      position={"top center"}
                                    >
                                      {eachPlat?.notes?.slice(0, 50)}...
                                    </Tooltip>
                                  ) : (
                                    <span>{eachPlat?.notes || "-"}</span>
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  <span>
                                    {eachPlat?.status ? (
                                      <span
                                        className="inline-flex py-[4px] px-[12px] rounded-[6px] text-[10px] font-bold uppercase"
                                        style={
                                          eachPlat?.status?.toLowerCase() ==
                                          "completed"
                                            ? {
                                                background: "#e8f5e9",
                                                color: "#388e3c",
                                              }
                                            : eachPlat?.status?.toLowerCase() ==
                                                "pending"
                                              ? {
                                                  background: "#dbe0f5",
                                                  color: "#335CFF",
                                                }
                                              : eachPlat?.status?.toLowerCase() ==
                                                  "overdue"
                                                ? {
                                                    background: "#fff3e0",
                                                    color: "#e65100",
                                                  }
                                                : {
                                                    background: "#fff3e0",
                                                    color: "#e65100",
                                                  }
                                        }
                                      >
                                        {eachPlat.status}
                                      </span>
                                    ) : (
                                      "-"
                                    )}
                                  </span>
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    ) : (
                      <DataNotFound title={"Leads"} />
                    )}
                  </div>
                ) : (
                  <InnerLoader />
                )}
              </ChartMainCard>
            </div>
          </TableCard>
        </div>
      </div>
    </>
  );
}
